<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <date-picker
              v-model="filters.year"
              :is-busy="isBusy"
              type="year"
              value-type="format"
              format="YYYY"
              placeholder="Select Year"
              style="min-width: 200px;"
            />
          </div>
          <div class="flex items-center w-full">
            <select-with-search
              :is-busy="isBusy"
              v-model="filters.owner_name"
              placeholder="Select owner name"
              style="min-width: 200px;"
              :options="ownerOptions"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5"
        @fetch="fetchCombinations()"
        ref="tableTop"
      >
        <template #start>
          <vs-button
            class="button shadow-primary font-semibold"
            color="primary"
            @click="addNewCombination"
            v-show="isUserCanAccessMenu(permission.name, 'add')"
          >
            Add New Combination
          </vs-button>
          <vs-button
            v-show="true"
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :loading="isBusy"
        :table-header="tableHeaders"
        :table-data="combinations"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :expanded-rows-list="expandedRowsList"
        :is-expanded-row-keep-same-header-col="true"
        :frozen-columns="isActionColumnVisible ? 5 : 4"
        :grouped-headers="[
          {
            title: 'Tugboat Information',
            startColumn: 'tug_horse_power',
            endColumn: 'tug_sending_data_gps',
            backgroundColor: '#E8F5E9'
          },
          {
            title: 'Barge Information',
            startColumn: 'barge_size',
            endColumn: 'barge_plan_load',
            backgroundColor: '#E3F2FD'
          }
        ]"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #tug="{ row }">
          {{ row.tug }}
        </template>
        <template #barge="{ row }">
          {{ row.barge }}
        </template>
        <template #fleet="{ row }">
          {{ row.fleet || '-' }}
        </template>
        <template #tug_owner="{ row }">
          {{ row.tug_owner || '-' }}
        </template>
        <template #barge_owner="{ row }">
          {{ row.barge_owner || '-' }}
        </template>
        <template #owner_name="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 18rem;"
            >
              <select-with-search
                v-model="row.editForm.owner_name.value"
                :options="ownerOptions"
                :disabled="isProcessing"
                placeholder="Select owner"
                @input="handleOwnerChange(row)"
              />
              <span
                v-if="!row.editForm.owner_name.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.owner_name || '-' }}
          </template>
        </template>
        <template #tug_name="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 12rem;"
            >
              <select-with-search
                v-model="row.editForm.tug_name.value"
                :options="getFilteredTugOptions(row)"
                :value="row.tug_name"
                :is-busy="isDropdownBusy.tug"
                :disabled="isProcessing"
                placeholder="Select tug"
                @input="handleTugChange(row)"
              />
              <span
                v-if="!row.editForm.tug_name.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.tug_name || '-' }}
          </template>
        </template>
        <template #barge_name="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 12rem;"
            >
              <select-with-search
                v-model="row.editForm.barge_name.value"
                :options="getFilteredBargeOptions(row)"
                :value="row.barge_name"
                :is-busy="isDropdownBusy.barge"
                :disabled="isProcessing"
                placeholder="Select barge"
                @input="handleBargeChange(row)"
              />
              <span
                v-if="!row.editForm.barge_name.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.barge_name || '-' }}
          </template>
        </template>
        <template #fleet_id="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  v-model="row.editForm.fleet_id.value"
                  :class="{ 'is-not-valid': !row.editForm.fleet_id.isValid }"
                  :disabled="isProcessing"
                  class="w-32"
                  placeholder="Enter fleet ID"
                />
              </div>
              <span
                v-if="!row.editForm.fleet_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.fleet_id || '-' }}
          </template>
        </template>
        <template #contract_number="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-42">
              <select-with-search
                v-model="row.editForm.contract_number.value"
                :options="contractOptions"
                :class="{ 'is-not-valid': !row.editForm.contract_number.isValid }"
                :is-busy="isDropdownBusy.contract"
                :disabled="isProcessing"
                placeholder="Select contract"
                @input="handleContractChange(row)"
              />
              <span
                v-if="!row.editForm.contract_number.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.contract_number || '-' }}
          </template>
        </template>
        <template #contract_hiring_cost="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-42">
              <vs-input
                v-model="row.editForm.contract_hiring_cost.value"
                disabled
                placeholder="Auto-filled from contract"
              />
            </div>
          </template>
          <template v-else>
            {{ row.contract_hiring_cost || '-' }}
          </template>
        </template>
        <template #agency_boct_name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-42">
              <select-with-search
                v-model="row.editForm.agency_boct_id.value"
                :options="agencyOptions"
                :class="{ 'is-not-valid': !row.editForm.agency_boct_id.isValid }"
                :disabled="isProcessing"
                placeholder="Select agency BOCT"
                @input="handleAgencyBoCTChange(row)"
              />
              <span
                v-if="!row.editForm.agency_boct_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.agency_boct_name || '-' }}
          </template>
        </template>
        <template #agency_smd_name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-42">
              <select-with-search
                v-model="row.editForm.agency_smd_id.value"
                :options="agencyOptions"
                :class="{ 'is-not-valid': !row.editForm.agency_smd_id.isValid }"
                :disabled="isProcessing"
                placeholder="Select agency SMD"
                @input="handleAgencySMDChange(row)"
              />
              <span
                v-if="!row.editForm.agency_smd_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.agency_smd_name || '-' }}
          </template>
        </template>
        <template #security_vendor_name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-42">
              <select-with-search
                v-model="row.editForm.security_vendor_id.value"
                :options="securityVendorOptions"
                :class="{ 'is-not-valid': !row.editForm.security_vendor_id.isValid }"
                :disabled="isProcessing"
                placeholder="Select security vendor"
                @input="handleSecurityVendorChange(row)"
              />
              <span
                v-if="!row.editForm.security_vendor_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.security_vendor_name || '-' }}
          </template>
        </template>
        <template #tug_horse_power="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_horse_power.value"
                type="number"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_horse_power || '-' }}
          </template>
        </template>
        <template #tug_fuel_liter_hour="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_fuel_liter_hour.value"
                type="number"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_fuel_liter_hour || '-' }}
          </template>
        </template>
        <template #tug_class="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_class.value"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_class || '-' }}
          </template>
        </template>
        <template #tug_grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_grt.value"
                type="number"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_grt || '-' }}
          </template>
        </template>
        <template #tug_build_year="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_build_year.value"
                type="number"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_build_year || '-' }}
          </template>
        </template>
        <template #tug_draft="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.tug_draft.value"
                type="number"
                step="0.1"
                disabled
                placeholder="Auto-filled from tug"
              />
            </div>
          </template>
          <template v-else>
            {{ row.tug_draft || '-' }}
          </template>
        </template>
        <template #tug_sending_data_gps="{ row }">
          <template v-if="row.isEditing">
            <div class="relative flex justify-center">
              <div>
                <img
                  alt="check"
                  :src="row.editForm.tug_sending_data_gps.value ? require('@/assets/shipo-icon-checkbox-true.svg') : require('@/assets/shipo-icon-checkbox-false.svg')"
                  class="w-8 h-8"
                >
              </div>
            </div>
          </template>
          <template v-else>
            <div class="flex justify-center">
              <div>
                <img
                  alt="check"
                  :src="row.tug_sending_data_gps ? require('@/assets/shipo-icon-checkbox-true.svg') : require('@/assets/shipo-icon-checkbox-false.svg')"
                  class="w-8 h-8"
                >
              </div>
            </div>
          </template>
        </template>
        <template #barge_size="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_size.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_size || '-' }}
          </template>
        </template>
        <template #barge_grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_grt.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_grt || '-' }}
          </template>
        </template>
        <template #barge_build_year="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_build_year.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_build_year || '-' }}
          </template>
        </template>
        <template #barge_class="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_class.value"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_class || '-' }}
          </template>
        </template>
        <template #barge_length="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_length.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_length || '-' }}
          </template>
        </template>
        <template #barge_beam="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_beam.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_beam || '-' }}
          </template>
        </template>
        <template #barge_depth="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_depth.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_depth || '-' }}
          </template>
        </template>
        <template #barge_load_grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_load_grt.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_load_grt || '-' }}
          </template>
        </template>
        <template #barge_min_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_min_load.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_min_load || '-' }}
          </template>
        </template>
        <template #barge_max_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_max_load.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_max_load || '-' }}
          </template>
        </template>
        <template #barge_plan_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.barge_plan_load.value"
                type="number"
                disabled
                placeholder="Auto-filled from barge"
              />
            </div>
          </template>
          <template v-else>
            {{ row.barge_plan_load || '-' }}
          </template>
        </template>
        <template #is_active="{ row }">
          <template v-if="row.isEditing">
            <vs-switch
              :disabled="isProcessing"
              v-model="row.editForm.is_active.value"
              color="secondary"
            />
          </template>
          <template v-else>
            <div
              :class="{
                'bg-green-200 text-green-700 border-green-400': row.is_active,
                'bg-red-200 text-red-700 border-red-400': !row.is_active
              }"
              class="button-l-plan w-20 items-center"
            >
              <span>{{ stateOptions[row.is_active] }}</span>
            </div>
          </template>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="popUp = { isShown: true, tempId: row.id, tempName: `${row.tug_name} - ${row.barge_name}` }"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
              <div
                v-if="isUserCanAccessMenu(permission.name, 'view')"
                :class="`self-center flex items-center justify-center cursor-pointer icon-transparent-bg`"
                @click="toggleRow(row)"
              >
                <img
                  :src="
                    require(
                      expandedRowsList.includes(row.id)
                        ? '@/assets/shipo-icon-arrow-up.svg'
                        : '@/assets/shipo-icon-arrow-down.svg'
                    )"
                  alt="arrow"
                >
              </div>
            </template>
          </div>
        </template>
        <!-- TEMPLATE FOR HISTORY ROW: FROZEN COLUMNS -->
        <template #detail-frozen="{ parentData }">
          <template
            v-if="parentData.histories.length === 0"
          >
            <tr class="history-row frozen">
              <td
                class="td"
                style="min-width: 50px;"
              />
              <td class="td">
                <div class="flex flex-row">
                  <div
                    class="flex items-center w-full justify-center"
                  >
                    <img :src="require('@/assets/shipo-icon-arrow-forward.svg')">
                  </div>
                </div>
              </td>
              <td
                :colspan="tableHeaders.filter((header) => header.freeze).length - 1"
                class="td"
              >
                <div class="italic">
                  No history found for this Tug & Barge Combination
                </div>
              </td>
            </tr>
          </template>
          <template
            v-else
            v-for="(history, indextr) in parentData.histories"
          >
            <tr
              :key="`${parentData.id}_${indextr}`"
              class="history-row frozen"
            >
              <td
                class="td"
                style="min-width: 50px;"
              />
              <td class="td">
                <div class="flex flex-row">
                  <div
                    class="flex items-center w-full justify-center"
                  >
                    <img :src="require('@/assets/shipo-icon-arrow-forward.svg')">
                  </div>
                </div>
              </td>
              <template
                v-for="(header, indextd) in tableHeaders.filter((th) => th.freeze && th.key !== 'no' && th.key !== 'action')"
              >
                <td
                  :key="`${indextd}_${header.key}`"
                  class="td"
                >
                  {{ history[header.key] }}
                </td>
              </template>
            </tr>
          </template>
        </template>
        <template #detail="{ parentData }">
          <template
            v-if="parentData.histories.length === 0"
          >
            <tr class="history-row scrollable">
              <td
                :colspan="tableHeaders.filter((header) => !header.freeze).length"
                class="td"
              />
            </tr>
          </template>
          <template
            v-else
            v-for="(history, indextr) in parentData.histories"
          >
            <tr
              :key="`${parentData.id}_${indextr}`"
              class="history-row scrollable"
            >
              <template v-for="(header, indextd) in tableHeaders.filter((h) => !h.freeze && h.key !== 'no' && h.key !== 'action')">
                <td
                  :key="`${indextd}_${header.key}`"
                  class="td"
                >
                  <template v-if="header.key === 'tug_sending_data_gps'">
                    <div class="flex justify-center">
                      <div>
                        <img
                          alt="check"
                          :src="
                            history.tug_sending_data_gps
                              ? require('@/assets/shipo-icon-checkbox-true.svg')
                              : require('@/assets/shipo-icon-checkbox-false.svg')
                          "
                          class="w-8 h-8"
                        >
                      </div>
                    </div>
                  </template>

                  <!-- DEFAULT -->
                  <template v-else>
                    <span>
                      {{ history[header.key] }}
                    </span>
                  </template>
                </td>
              </template>
            </tr>
          </template>
        </template>
      </data-table>
      <table-bottom @fetch="fetchCombinations" />
    </div>
    <pop-up
      is-warning
      title="Delete Combination"
      left-button="Cancel"
      right-button="Yes, Delete"
      :active.sync="popUp.isShown"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteCombination"
    >
      <p class="text-center mt-1">
        Are you sure want to delete "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUp.isRestoreShown"
      :is-busy="isProcessing"
      is-warning
      title="Restore Combination"
      left-button="Cancel"
      right-button="Restore"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreCombination"
    >
      <p class="text-center mt-1">
        {{ `The combination "${restoreData.tug_name} - ${restoreData.barge_name}" already exists but has been deleted.` }}
      </p>
      <p class="text-center">
        Would you like to restore it?
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
    >
      You have successfully {{ popUpSuccessAction }} combination
    </pop-up>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    TableBottom,
    TableTop,
    DataTable,
    SelectWithSearch,
    PopUp,
    DatePicker
  },
  data() {
    return {
      isBusy: false,
      isProcessing: false,
      isDropdownBusy: {
        tug: false,
        barge: false,
        contract: false
      },
      isAdding: false,
      isDownload: false,
      popUp: {
        isShown: false,
        isRestoreShown: false,
        tempId: null,
        tempName: null
      },
      restoreData: {
        id: null,
        tug_name: null,
        barge_name: null,
        formData: {}
      },
      popUpSuccess: false,
      popUpSuccessAction: 'deleted',
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px', freeze: true },
        { key: 'owner_name', label: 'Owner Name', width: '150px', sortable: true, freeze: true },
        { key: 'tug_name', label: 'Tug Name', width: '120px', sortable: true, freeze: true },
        { key: 'barge_name', label: 'Barge Name', width: '120px', sortable: true, freeze: true },
        { key: 'fleet_id', label: 'Fleet ID', width: '120px', sortable: true },
        { key: 'contract_number', label: 'Contract Number', width: '120px', sortable: true },
        { key: 'agency_boct_name', label: 'Agency BOCT', width: '100px', sortable: true },
        { key: 'agency_smd_name', label: 'Agency SMD', width: '100px', sortable: true },
        { key: 'security_vendor_name', label: 'Security Vendor', width: '100px', sortable: true },
        { key: 'contract_hiring_cost', label: 'Contracted Hiring Cost (IDR)', width: '150px', sortable: true },
        { key: 'updated_by', label: 'Updated By', width: '150px', sortable: false },
        { key: 'updated_at', label: 'Updated At', width: '150px', sortable: false },
        { key: 'tug_horse_power', label: 'Horsepower', width: '90px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_fuel_liter_hour', label: 'Fuel Litre/Hour', width: '90px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_class', label: 'Class Tag', width: '80px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_grt', label: 'GRT Tugboat', width: '90px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_build_year', label: 'Tug Built', width: '80px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_draft', label: 'Draft Tugboat (m)', width: '100px', sortable: true, group: 'Tugboat Information' },
        { key: 'tug_sending_data_gps', label: 'Sending Data GPS', width: '100px', sortable: true, group: 'Tugboat Information' },
        { key: 'barge_size', label: 'Size Barge', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_grt', label: 'GRT Barge', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_build_year', label: 'Barge Built', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_class', label: 'Class Barge', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_length', label: 'Length', width: '70px', sortable: true, group: 'Barge Information' },
        { key: 'barge_beam', label: 'Beam', width: '70px', sortable: true, group: 'Barge Information' },
        { key: 'barge_depth', label: 'Depth', width: '70px', sortable: true, group: 'Barge Information' },
        { key: 'barge_load_grt', label: 'Load GRT', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_min_load', label: 'Min Load', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_max_load', label: 'Max Load', width: '80px', sortable: true, group: 'Barge Information' },
        { key: 'barge_plan_load', label: 'Plan Load', width: '80px', sortable: true, group: 'Barge Information' }
      ],
      stateOptions: {
        true: 'Active',
        false: 'Inactive'
      },
      combinations: [],
      allCombinations: [],
      ownerOptions: [],
      filters: {
        sort_by: 'owner_name',
        sort_dir: 'asc',
        year: String(new Date().getFullYear()),
        owner_name: '',
        search: '',
        page: 1,
        limit: 10,
        is_get_deleted: false
      },
      tugOptions: [],
      bargeOptions: [],
      tugs: [],
      barges: [],
      contractOptions: [],
      contracts: [],
      agencies: [],
      agencyOptions: [],
      expandedRowsList: [],
      securityVendorOptions: []
    }
  },
  computed: {
    ...mapGetters({
      totalPage: 'Table/totalPage',
      totalData: 'Table/totalData',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      owners: 'TugManagement/owners',
      tugBargesFiles: 'TugBargesCombination/tugBargesFiles',
      allTugBarges: 'TugBargesCombination/allTugBarges',
      securityVendors: 'TugBargesCombination/securityVendors'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  watch: {
    pageSize: {
      handler(val) {
        this.filters.limit = val;
        this.filters.page = 1;
        this.fetchCombinations();
      }
    },
    currentPage: {
      handler(val) {
        this.filters.page = val;
        this.fetchCombinations();
      }
    },
    owners: {
      handler(val) {
        if (val) {
          this.ownerOptions = val.map(item => ({
            text: item.owner_name,
            value: item.owner_name
          }));
        }
      }
    },
    tugs: {
      handler(val) {
        if (val) {
          this.tugOptions = val.map(item => ({
            text: item.name,
            value: item.name
          }));
        }
      },
      immediate: true
    },
    barges: {
      handler(val) {
        this.bargeOptions = val.map(item => ({
          text: item.name,
          value: item.name
        }));
      }
    },
    contracts: {
      handler(val) {
        this.contractOptions = val.map(item => ({
          text: item.contract_number,
          value: item.contract_number
        }));
      }
    },
    agencies: {
      handler(val) {
        this.agencyOptions = val.map(item => ({
          text: item.name,
          value: item.id
        }));
      }
    },
    securityVendors: {
      handler(val) {
        this.securityVendorOptions = val.map(item => ({
          text: item.name,
          value: item.id
        }));
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadTugBarges()
        }
      }
    },
    filters: {
      handler(newVal) {
        this.fetchCombinations();
      },
      deep: true
    },
    tugBargesFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `ListTugBarges.xlsx`)
          this.$store.dispatch('TugBargesCombination/resetTugBargeFiles');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.tableHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.tableHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.tableHeaders.splice(index, 1);
        }
      }
    }
  },
  async created() {
    await this.fetchOwners();
    await this.fetchAgencies();
    await this.fetchInitialCombinations();
    await this.fetchCombinations();
    await this.fetchSecurityVendors();
  },
  methods: {
    async fetchInitialCombinations() {
      try {
        this.isBusy = true;
        const response = await this.$store.dispatch('TugBargesCombination/fetchTugBarges', {
          sort_dir: this.filters.sort_dir,
          sort_by: this.filters.sort_by,
          is_get_deleted: this.filters.is_get_deleted,
          is_get_all: true,
          page: 1,
          limit: 1000
        });

        if (response?.data) {
          const mappedData = response.data.map(item => ({
            id: item.id,
            owner_name: item.owner_name,
            tug_name: item.tug_name,
            barge_name: item.barge_name,
            fleet_id: item.fleet_id,
            contract_number: item.contract_number,
            agency_boct_name: item.agency_boct_name,
            agency_smd_name: item.agency_smd_name,
            contract_hiring_cost: item.contract_hiring_cost,
            tug_horse_power: item.tug?.tug_horse_power,
            tug_fuel_liter_hour: item.tug?.tug_fuel_liter_hour,
            tug_class: item.tug?.tug_class,
            tug_grt: item.tug?.tug_grt,
            tug_build_year: item.tug?.tug_build_year,
            tug_draft: item.tug?.tug_draft,
            tug_sending_data_gps: item.tug?.tug_sending_data_gps || false,
            barge_size: item.barge?.barge_size,
            barge_grt: item.barge?.barge_grt,
            barge_build_year: item.barge?.barge_build_year,
            barge_class: item.barge?.barge_class,
            barge_length: item.barge?.barge_length,
            barge_beam: item.barge?.barge_beam,
            barge_depth: item.barge?.barge_depth,
            barge_load_grt: item.barge?.barge_load_grt,
            barge_min_load: item.barge?.barge_min_load,
            barge_max_load: item.barge?.barge_max_load,
            barge_plan_load: item.barge?.barge_plan_load
          }));
          
          this.allCombinations = [...mappedData];
        } else {
          this.allCombinations = [];
        }
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch combinations, please try again');
        this.allCombinations = [];
      } finally {
        this.isBusy = false;
      }
    },
    async fetchOwners() {
      try {
        await this.$store.dispatch('TugManagement/fetchOwners', {
          is_active: true,
          is_get_deleted: false,
          page: 1,
          limit: 1000
        });
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch owners');
      }
    },
    async fetchAgencies() {
      try {
        const data = await this.$store.dispatch('TugBargesCombination/fetchAgencies', {
          is_master_data: true,
          sort_by: 'name',
          sort_dir: 'asc',
          status: true
        });
        this.agencies = data;
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch agencies');
      }
    },
    async fetchSecurityVendors() {
      try {
        const data = await this.$store.dispatch('TugBargesCombination/fetchSecurityVendors')
      }
      catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch security vendors');
      }
    },
    addNewCombination() {
      if (this.combinations.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }
      this.tugs = [];
      this.tugOptions = [];
      this.barges = [];
      this.bargeOptions = [];
      this.contractOptions = [];
      this.contracts = [];
      
      const newRow = {
        id: `new-${Date.now()}`,
        owner_name: '',
        owner_id: null,
        tug_name: '',
        barge_name: '',
        fleet_id: '',
        contract_number: '',
        agency_boct_name: '',
        agency_boct_id: null,
        agency_smd_name: '',
        agency_smd_id: null,
        contract_hiring_cost: '',
        tug_horse_power: '',
        tug_fuel_liter_hour: '',
        tug_class: '',
        tug_grt: '',
        tug_build_year: '',
        tug_draft: '',
        tug_sending_data_gps: '',
        barge_size: '',
        barge_grt: '',
        barge_build_year: '',
        barge_class: '',
        barge_length: '',
        barge_beam: '',
        barge_depth: '',
        barge_load_grt: '',
        barge_min_load: '',
        barge_max_load: '',
        barge_plan_load: '',
        isEditing: true,
        editForm: {
          owner_name: { value: null, isValid: true },
          owner_id: { value: null, isValid: true },
          tug_name: { value: null, isValid: true },
          tug_id: { value: null, isValid: true },
          barge_name: { value: null, isValid: true },
          barge_id: { value: null, isValid: true},
          fleet_id: { value: '', isValid: true },
          contract_number: { value: '', isValid: true },
          agency_boct_name: { value: '', isValid: true },
          agency_boct_id: { value: '', isValid: true },
          agency_smd_id: { value: '', isValid: true },
          agency_smd_name: { value: '', isValid: true },
          contract_hiring_cost: { value: '', isValid: true },
          contract_id: { value: '', isValid: true },
          security_vendor_name: { value: '', isValid: true },
          security_vendor_id: { value: '', isValid: true },
          tug_horse_power: { value: '' },
          tug_fuel_liter_hour: { value: '' },
          tug_class: { value: '' },
          tug_grt: { value: '' },
          tug_build_year: { value: '' },
          tug_draft: { value: '' },
          tug_sending_data_gps: { value: '' },
          barge_size: { value: '' },
          barge_grt: { value: '' },
          barge_build_year: { value: '' },
          barge_class: { value: '' },
          barge_length: { value: '' },
          barge_beam: { value: '' },
          barge_depth: { value: '' },
          barge_load_grt: { value: '' },
          barge_min_load: { value: '' },
          barge_max_load: { value: '' },
          barge_plan_load: { value: ''}
        }
      };

      this.isAdding = true;
      this.combinations.unshift(newRow);
    },
    async startEdit(row) {
      if (this.combinations.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      const index = this.combinations.findIndex(item => item.id === row.id);
      if (index === -1) return;

      this.$set(this.combinations[index], 'isEditing', true);
      this.$set(this.combinations[index], 'editForm', {
        owner_name: { value: '', isValid: true },
        owner_id: { value: '', isValid: true },
        tug_name: { value: '', isValid: true },
        tug_id: { value: '', isValid: true },
        barge_name: { value: '', isValid: true },
        barge_id: { value: '', isValid: true },
        fleet_id: { value: '', isValid: true },
        contract_number: { value: '', isValid: true },
        contract_id: { value: '', isValid: true },
        contract_hiring_cost: { value: '', isValid: true },
        agency_boct_name: { value: '', isValid: true },
        agency_boct_id: { value: '', isValid: true },
        agency_smd_name: { value: '', isValid: true },
        agency_smd_id: { value: '', isValid: true },
        security_vendor_name: { value: '', isValid: true },
        security_vendor_id: { value: '', isValid: true },
        tug_horse_power: { value: '' },
        tug_fuel_liter_hour: { value: '' },
        tug_class: { value: '' },
        tug_grt: { value: '' },
        tug_build_year: { value: '' },
        tug_draft: { value: '' },
        tug_sending_data_gps: { value: '' },
        barge_size: { value: '' },
        barge_grt: { value: '' },
        barge_build_year: { value: '' },
        barge_class: { value: '' },
        barge_length: { value: '' },
        barge_beam: { value: '' },
        barge_depth: { value: '' },
        barge_load_grt: { value: '' },
        barge_min_load: { value: '' },
        barge_max_load: { value: '' },
        barge_plan_load: { value: '' }
      });

      try {
        if (row.owner_name || row.owner_id) {
          this.isDropdownBusy.tug = true;
          const tugResponse = await this.$store.dispatch('TugBargesCombination/fetchTugBargesDropdown', {
            data: 'tugs',
            owner_name: row.owner_name,
            owner_id: row.owner_id
          });
          this.isDropdownBusy.tug = false;
          if (tugResponse) {
            this.tugs = tugResponse;
          }
          this.isDropdownBusy.barge = true;
          const bargeResponse = await this.$store.dispatch('TugBargesCombination/fetchTugBargesDropdown', {
            data: 'barges',
            owner_name: row.owner_name,
            owner_id: row.owner_id
          });
          this.isDropdownBusy.barge = false;
          if (bargeResponse) {
            this.barges = bargeResponse;
          }

          if (row.tug_name && row.barge_name) {
            const selectedTug = this.tugs.find(tug => tug.name === row.tug_name);
            const selectedBarge = this.barges.find(barge => barge.name === row.barge_name);

            if (selectedTug?.id && selectedBarge?.id) {
              this.isDropdownBusy.contract = true;
              const contractResponse = await this.$store.dispatch('TugBargesCombination/fetchContractDropdown', {
                tug_id: selectedTug.id,
                barge_id: selectedBarge.id
              });
              this.isDropdownBusy.contract = false;
              if (contractResponse) {
                this.contracts = contractResponse.filter(contract => contract.status === 'active');
              }
            }
          }
          const editForm = {
            owner_name: { value: row.owner_name, isValid: true },
            owner_id: {
              value: row.owner_name
                ? this.owners.find(o => o.owner_name === row.owner_name)?.owner_id
                : '',
              isValid: true
            },
            tug_name: { value: row.tug_name || '', isValid: true },
            tug_id: {
              value: row.tug_name
                ? this.tugs.find(option => option.name === row.tug_name)?.id
                : '',
              isValid: true
            },
            barge_name: { value: row.barge_name || '', isValid: true },
            barge_id: {
              value: row.barge_name
                ? this.barges.find(option => option.name === row.barge_name)?.id
                : '',
              isValid: true
            },
            fleet_id: { value: row.fleet_id || '', isValid: true },
            contract_number: { value: row.contract_number || '', isValid: true },
            contract_id: {
              value: row.contract_number
                ? this.contracts.find(option => option.contract_number === row.contract_number)?.id
                : '',
              isValid: true
            },
            contract_hiring_cost: { 
              value: row.contract_number
                ? this.contracts.find(option => option.contract_number === row.contract_number)?.contract_hiring_cost
                : '',
              isValid: true 
            },
            agency_boct_name: { value: row.agency_boct_name || '', isValid: true },
            agency_boct_id: {
              value: row.agency_boct_id
                ? row.agency_boct_id
                : this.agencies.find(option => option.name === row.agency_boct_name)?.id || '',
              isValid: true
            },
            agency_smd_name: { value: row.agency_smd_name || '', isValid: true},
            agency_smd_id: {
              value: row.agency_smd_id
                ? row.agency_smd_id
                : this.agencies.find(option => option.name === row.agency_smd_name)?.id || '',
              isValid: true
            },
            security_vendor_name: { value: row.security_vendor_name || '', isValid: true },
            security_vendor_id: {
              value: row.security_vendor_id
                ? row.security_vendor_id
                : this.securityVendors.find(option => option.name === row.security_vendor_name)?.id || '',
              isValid: true
            },
            tug_horse_power: { value: row.tug_horse_power || '' },
            tug_fuel_liter_hour: { value: row.tug_fuel_liter_hour || '' },
            tug_class: { value: row.tug_class || '' },
            tug_grt: { value: row.tug_grt || '' },
            tug_build_year: { value: row.tug_build_year || '' },
            tug_draft: { value: row.tug_draft || '' },
            tug_sending_data_gps: { value: row.tug_sending_data_gps || '' },
            barge_size: { value: row.barge_size || '' },
            barge_grt: { value: row.barge_grt || '' },
            barge_build_year: { value: row.barge_build_year || '' },
            barge_class: { value: row.barge_class || '' },
            barge_length: { value: row.barge_length || '' },
            barge_beam: { value: row.barge_beam || '' },
            barge_depth: { value: row.barge_depth || '' },
            barge_load_grt: { value: row.barge_load_grt || '' },
            barge_min_load: { value: row.barge_min_load || '' },
            barge_max_load: { value: row.barge_max_load || '' },
            barge_plan_load: { value: row.barge_plan_load || '' }
          };

          this.$set(this.combinations[index], 'editForm', editForm);
        }
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch options');
      }
    },
    cancelEdit(row) {
      if (this.isAdding) {
        const index = this.combinations.findIndex(item => item.id === row.id);
        if (index !== -1) {
          this.combinations.splice(index, 1);
        }
        this.isAdding = false;
      } else {
        const index = this.combinations.findIndex(item => item.id === row.id);
        if (index !== -1) {
          this.$set(this.combinations[index], 'isEditing', false);
          this.$set(this.combinations[index], 'editForm', null);
        }
      }
      this.isAdding = false;
    },
    cancelAllEdits() {
      if (this.isAdding) {
        const index = this.combinations.findIndex(row => row.isEditing);
        if (index !== -1) {
          this.combinations.splice(index, 1);
        }
        this.isAdding = false;
      }

      this.combinations.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false);
          this.$set(row, 'editForm', null);
        }
      });
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    populateCombinationsData(data) {
      return {
        id: data.id,
        owner_name: data.owner_name,
        owner_id: data.owner_id,
        tug_name: data.tug_name,
        barge_name: data.barge_name,
        fleet_id: data.fleet_id,
        histories: [],
        contract_number: data.contract_number,
        agency_boct_name: data.agency_boct_name,
        agency_boct_id: data.agency_boct_id,
        agency_smd_name: data.agency_smd_name,
        agency_smd_id: data.agency_smd_id,
        security_vendor_name: data.security_vendor_name,
        security_vendor_id: data.security_vendor_id,
        contract_hiring_cost: data.contract_hiring_cost,
        updated_at: moment(data.updated_at).format('DD-MMM-YYYY HH:mm'),
        updated_by: data.updated_by
      }
    },
    async fetchCombinations() {
      if (this.isBusy) return;

      try {
        this.isBusy = true;
        const response = await this.$store.dispatch('TugBargesCombination/fetchTugBarges', {
          ...this.filters,
          is_get_all: false
        });

        if (response?.data) {
          this.combinations = response.data.map(item => ({
            ...this.populateCombinationsData(item),
            ...item.tug,
            ...item.barge,
            ...(item.histories.length && { histories: item.histories.map(history => ({
              ...this.populateCombinationsData(history),
              ...this.handleHistoryData(history),
              updated_at: moment(history.updated_at).format('DD-MMM-YYYY HH:mm')
            })) })
          }));
        } else {
          this.combinations = [];
        }
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch combinations, please try again');
        this.combinations = [];
      } finally {
        this.isAdding = false;
        this.isBusy = false;
      }
    },
    handleHistoryData(history) {
      return {
        tug_horse_power: history.tug?.tug_horse_power,
        tug_fuel_liter_hour: history.tug?.tug_fuel_liter_hour,
        tug_class: history.tug?.tug_class,
        tug_grt: history.tug?.tug_grt,
        tug_build_year: history.tug?.tug_build_year,
        tug_draft: history.tug?.tug_draft,
        tug_sending_data_gps: history.tug?.tug_sending_data_gps,
        barge_size: history.barge?.barge_size,
        barge_grt: history.barge?.barge_grt,
        barge_build_year: history.barge?.barge_build_year,
        barge_class: history.barge?.barge_class,
        barge_length: history.barge?.barge_length,
        barge_beam: history.barge?.barge_beam,
        barge_depth: history.barge?.barge_depth,
        barge_load_grt: history.barge?.barge_load_grt,
        barge_min_load: history.barge?.barge_min_load,
        barge_max_load: history.barge?.barge_max_load,
        barge_plan_load: history.barge?.barge_plan_load
      }
    },
    async downloadTugBarges() {
      try {
        this.isBusy = true
        await this.$store.dispatch('TugBargesCombination/downloadTugBarges', {
          sort_by: this.filters.sort_by || 'owner_name',
          sort_dir: this.filters.sort_dir || 'asc',
          year: this.filters.year || '2025',
          owner_name: this.filters.owner_name || null
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false
      } catch (error) {
        this.isDownload = false
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download Tug Barge, please try again')
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: 'owner_name',
        sort_dir: 'asc',
        year: String(new Date().getFullYear()),
        owner_name: null,
        search: '',
        page: 1,
        limit: 10,
        is_get_deleted: false
      };
      this.$refs.tableTop.$emit('reset-search')
      this.allCombinations = [];
      this.fetchCombinations();
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchCombinations();
    },
    async deleteCombination() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('TugBargesCombination/deleteTugBarge', this.popUp.tempId);
        this.showSuccessNotification(this, 'Successfully deleted combination');
        this.isProcessing = false;
        this.popUp.isShown = false;
        this.popUp.tempId = null;
        await this.fetchInitialCombinations();
        await this.fetchCombinations();
      } catch (error) {
        this.isProcessing = false;
        this.showFailedNotification(this, error?.data?.error?.message || error?.error || 'Failed to delete combination, please try again');
      }
    },
    async handleOwnerChange(row) {
      row.editForm.tug_name.value = null;
      row.editForm.barge_name.value = null;
      row.editForm.contract_number.value = null;
      row.editForm.contract_hiring_cost.value = '';
      this.tugs = [];
      this.tugOptions = [];
      this.barges= [];
      this.bargeOptions = [];
      this.contractOptions = [];
      this.contracts = [];

      if (!row.editForm.owner_name.value) {
        this.tugOptions = [];
        this.bargeOptions = [];
        return;
      }
      if (row.editForm.owner_name.value) {
        const owner = this.owners.find(c => c.owner_name === row.editForm.owner_name.value);
        if (owner) {
          row.editForm.owner_id.value = owner.owner_id;
        }
      } else {
        row.editForm.owner_id.value = '';
      }

      try {
        this.isDropdownBusy.tug = true;
        const tugResponse = await this.$store.dispatch('TugBargesCombination/fetchTugBargesDropdown', {
          data: 'tugs',
          owner_name: row.editForm.owner_name.value
        });
        this.isDropdownBusy.tug = false;
        if (tugResponse) {
          this.tugs = tugResponse
        }

        this.isDropdownBusy.barge = true;

        const bargeResponse = await this.$store.dispatch('TugBargesCombination/fetchTugBargesDropdown', {
          data: 'barges',
          owner_name: row.editForm.owner_name.value
        });

        this.isDropdownBusy.barge = false;

        if (bargeResponse) {
          this.barges = bargeResponse
        }
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch tugs and barges');
        this.tugOptions = [];
        this.bargeOptions = [];
      }
    },
    async handleTugChange(row) {
      row.editForm.contract_number.value = null;
      row.editForm.contract_hiring_cost.value = '';
      this.contractOptions = [];
      this.contracts = [];

      if (row.editForm.tug_name.value) {
        const tug = this.tugs.find(c => c.name === row.editForm.tug_name.value);
        if (tug) {
          row.editForm.tug_id.value = tug.id;
          row.editForm.tug_horse_power.value = tug.horsepower;
          row.editForm.tug_fuel_liter_hour.value = tug.fuel_litre_hour;
          row.editForm.tug_class.value = tug.class_tug;
          row.editForm.tug_grt.value = tug.grt_tugboat;
          row.editForm.tug_build_year.value = tug.tug_built;
          row.editForm.tug_draft.value = tug.draft_tugboat;
          row.editForm.tug_sending_data_gps.value = tug.sending_base || false;
        }
      } else {
        row.editForm.tug_id.value = '';
        row.editForm.tug_horse_power.value = '';
        row.editForm.tug_fuel_liter_hour.value = '';
        row.editForm.tug_class.value = '';
        row.editForm.tug_grt.value = '';
        row.editForm.tug_build_year.value = '';
        row.editForm.tug_draft.value = '';
        row.editForm.tug_sending_data_gps.value = false;
      }

      if (row.editForm.tug_name.value && row.editForm.barge_name.value) {
        try {
          this.fetchContracts(row);
        } catch (error) {
          this.showFailedNotification(this, error?.message || 'Failed to fetch contracts');
        }
      }
    },
    async handleBargeChange(row) {
      row.editForm.contract_number.value = null;
      row.editForm.contract_hiring_cost.value = '';
      this.contractOptions = [];
      this.contracts = [];

      if (row.editForm.barge_name.value) {
        const barge = this.barges.find(c => c.name === row.editForm.barge_name.value);
        if (barge) {
          row.editForm.barge_id.value = barge.id;
          row.editForm.barge_size.value = barge.size_barge;
          row.editForm.barge_grt.value = barge.grt_barge;
          row.editForm.barge_build_year.value = barge.barge_built;
          row.editForm.barge_class.value = barge.class_class;
          row.editForm.barge_length.value = barge.length;
          row.editForm.barge_beam.value = barge.beam;
          row.editForm.barge_depth.value = barge.depth;
          row.editForm.barge_load_grt.value = barge.load_grt;
          row.editForm.barge_min_load.value = barge.min_load;
          row.editForm.barge_max_load.value = barge.max_load;
          row.editForm.barge_plan_load.value = barge.plan_load;
        }
      } else {
        row.editForm.barge_id.value = '';
        row.editForm.barge_size.value = '';
        row.editForm.barge_grt.value = '';
        row.editForm.barge_build_year.value = '';
        row.editForm.barge_class.value = '';
        row.editForm.barge_length.value = '';
        row.editForm.barge_beam.value = '';
        row.editForm.barge_depth.value = '';
        row.editForm.barge_load_grt.value = '';
        row.editForm.barge_min_load.value = '';
        row.editForm.barge_max_load.value = '';
        row.editForm.barge_plan_load.value = '';
      }

      if (row.editForm.tug_name.value && row.editForm.barge_name.value) {
        try {
          this.fetchContracts(row);
        } catch (error) {
          this.showFailedNotification(this, error?.message || 'Failed to fetch contracts');
        }
      }
    },
    async fetchContracts(row) {
      try {
        if (!row.editForm.tug_name.value || !row.editForm.barge_name.value) {
          this.contractOptions = [];
          this.contracts = [];
          return;
        }

        const selectedTug = this.tugs.find(tug => tug.name === row.editForm.tug_name.value);
        const selectedBarge = this.barges.find(barge => barge.name === row.editForm.barge_name.value);

        if (!selectedTug?.id || !selectedBarge?.id) {
          this.contractOptions = [];
          this.contracts = [];
          return;
        }

        this.isDropdownBusy.contract = true;

        const response = await this.$store.dispatch('TugBargesCombination/fetchContractDropdown', {
          tug_id: selectedTug.id,
          barge_id: selectedBarge.id
        });

        if (response) {
          this.contracts = response.filter(contract => contract.status === 'active');
        } else {
          this.contractOptions = [];
          this.contracts = [];
        }
        this.isDropdownBusy.contract = false;
      } catch (error) {
        this.isDropdownBusy.contract = false;
        this.showFailedNotification(this, error?.message || 'Failed to fetch contracts');
        this.contractOptions = [];
        this.contracts = [];
      }
    },
    handleContractChange(row) {
      if (row.editForm.contract_number.value) {
        const contract = this.contracts.find(c => c.contract_number === row.editForm.contract_number.value);
        if (contract) {
          row.editForm.contract_hiring_cost.value = contract.contract_hiring_cost;
          row.editForm.contract_id.value = contract.id
        }
      } else {
        row.editForm.contract_hiring_cost.value = '';
        row.editForm.contract_id.value = '';
      }
    },

    handleAgencyBoCTChange(row) {
      if (row.editForm.agency_boct_id.value) {
        const boct = this.agencies.find(c => c.id === row.editForm.agency_boct_id.value);
        if (boct) {
          row.editForm.agency_boct_name.value = boct.name;
        }
      } else {
        row.editForm.agency_boct_name.value = '';
      }
    },
    handleAgencySMDChange(row) {
      if (row.editForm.agency_smd_id.value) {
        const smd = this.agencies.find(c => c.id === row.editForm.agency_smd_id.value);
        if (smd) {
          row.editForm.agency_smd_name.value = smd.name;
        }
      } else {
        row.editForm.agency_smd_name.value = '';
      }
    },
    handleSecurityVendorChange(row) {
      if (row.editForm.security_vendor_id.value) {
        const securityVendor = this.securityVendors.find(c => c.id === row.editForm.security_vendor_id.value);
        if (securityVendor) {
          row.editForm.security_vendor_name.value = securityVendor.name;
        }
      } else {
        row.editForm.security_vendor_name.value = '';
      }
    },
    async checkRestoreData(formData) {
      try {
        const response = await this.$store.dispatch('TugBargesCombination/fetchTugBarges', {
          tug_name: formData.tug_name,
          barge_name: formData.barge_name,
          is_get_deleted: true
        });
        if (response) {
          const { id, tug_name, barge_name } = response;
          this.restoreData.id = id;
          this.restoreData.tug_name = tug_name;
          this.restoreData.barge_name = barge_name;
          this.popUp.isRestoreShown = true;
          this.restoreData.formData = formData;
          return true;
        }
        return false;
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to restore data, please try again')
      }
    },
    closeRestoreModal() {
      this.popUp.isRestoreShown = false;
      this.restoreData = {
        id: null,
        tug_name: null,
        barge_name: null,
        formData: {}
      };
    },
    async restoreCombination() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('TugBargesCombination/updateTugBarge', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            is_restore: true
          }
        })
        this.showSuccessNotification(this, 'Successfully restored combination')
        this.isProcessing = false;
        this.closeRestoreModal()
        this.cancelAllEdits()
        await this.fetchInitialCombinations()
        await this.fetchCombinations()
      } catch (error) {
        this.isProcessing = false;
        this.showFailedNotification(this, error?.message || 'Failed to restore combination, please try again')
      }
    },
    resetTableFilter() {
      this.popUpSuccess = false;
      this.resetFilters();
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      const formData = {
        owner_name: row.editForm.owner_name.value,
        owner_id: this.owners.find(owner => owner.owner_name === row.editForm.owner_name.value)?.owner_id,
        tug_id: this.tugs.find(tug => tug.name === row.editForm.tug_name.value)?.id,
        tug_name: row.editForm.tug_name.value,
        barge_name: row.editForm.barge_name.value,
        barge_id: this.barges.find(barge => barge.name === row.editForm.barge_name.value)?.id,
        fleet_id: row.editForm.fleet_id.value,
        contract_number: row.editForm.contract_number.value,
        contract_id: this.contracts.find(c => c.contract_number === row.editForm.contract_number.value)?.id,
        contract_hiring_cost: row.editForm.contract_hiring_cost.value,
        agency_boct_name: row.editForm.agency_boct_name.value,
        agency_boct_id: row.editForm.agency_boct_id.value || this.agencies.find(agency => agency.name === row.editForm.agency_boct_name.value || agency.code === row.editForm.agency_boct_name.value)?.id,
        agency_smd_id: row.editForm.agency_smd_id.value || this.agencies.find(agency => agency.name === row.editForm.agency_smd_name.value || agency.code === row.editForm.agency_smd_name.value)?.id,
        agency_smd_name: row.editForm.agency_smd_name.value,
        security_vendor_id: row.editForm.security_vendor_id.value || this.securityVendors.find(securityVendor => securityVendor.name === row.editForm.security_vendor_name.value)?.id
      };

      if (!formData.tug_id || !formData.barge_id) {
        this.showFailedNotification(this, 'Please select both tug and barge');
        return;
      }

      const existingFleet = this.combinations.find(combo => 
        combo.fleet_id === formData.fleet_id && 
        combo.id !== row.id && 
        !combo.isEditing
      );
      
      if (existingFleet) {
        this.showFailedNotification(this, 'Fleet ID already exists');
        return;
      }

      try {
        if (row.tug_name !== formData.tug_name || row.barge_name !== formData.barge_name) {
          this.isProcessing = true;
          const isExist = await this.checkRestoreData(formData);
          if (isExist) {
            this.isProcessing = false;
            return
          }
        }

        if (this.isAdding) {
          await this.$store.dispatch('TugBargesCombination/createTugBarge', formData);
          this.showSuccessNotification(this, 'Successfully created combination');
        } else {
          await this.$store.dispatch('TugBargesCombination/updateTugBarge', {
            id: row.id,
            form: formData
          });
          this.showSuccessNotification(this, 'Successfully updated combination');
        }

        this.cancelAllEdits();
        this.isProcessing = false;
        await this.fetchInitialCombinations();
        await this.fetchCombinations();
      } catch (error) {
        this.isProcessing = false;
        const errorMessage = error?.message || error?.error?.errors.length ? error?.error?.errors?.map(err => err?.message)?.join(',') : 'Failed to save combination, please try again';
        this.showFailedNotification(this, errorMessage);
      }
    },
    getFilteredTugOptions(row) {
      if (!this.tugs.length) return [];
      
      const usedTugs = this.allCombinations
        .filter(combo => 
          combo.owner_name === row.editForm.owner_name.value && 
          !combo.isEditing && 
          combo.id !== row.id
        )
        .map(combo => combo.tug_name);

      const uniqueTugNames = [...new Set(this.tugs.map(tug => tug.name))];
      
      return uniqueTugNames
        .filter(name => 
          !usedTugs.includes(name) || 
          (row.isEditing && row.tug_name === name)
        )
        .map(name => ({
          text: name,
          value: name
        }));
    },

    getFilteredBargeOptions(row) {
      if (!this.barges.length) return [];
      
      const usedBarges = this.allCombinations
        .filter(combo => 
          combo.owner_name === row.editForm.owner_name.value && 
          !combo.isEditing && 
          combo.id !== row.id
        )
        .map(combo => combo.barge_name);

      const uniqueBargeNames = [...new Set(this.barges.map(barge => barge.name))];
      
      return uniqueBargeNames
        .filter(name => 
          !usedBarges.includes(name) || 
          (row.isEditing && row.barge_name === name)
        )
        .map(name => ({
          text: name,
          value: name
        }));
    },
    toggleRow(row) {
      const index = this.expandedRowsList.indexOf(row.id)
      if (index > -1) {
        row.isExpand = false
        this.expandedRowsList.splice(index, 1) // Collapse
      } else {
        row.isExpand = true
        this.expandedRowsList.push(row.id) // Expand
      }
    }
  }
}
</script>

<style scoped>
.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.is-not-valid {
  /deep/ .vs-con-input,
  /deep/ .mx-input-wrapper,
  /deep/ .bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.history-row {
  @apply h-12;

  &.frozen {
    .td {
      &:first-child {
        @apply pl-10;
        @apply rounded-l-2xl;
      }
    }
  }

  &.scrollable {
    .td {
      &:last-child {
        @apply pr-10;
        @apply rounded-r-2xl;
      }
    }
  }

  .td {
    background: #FEF3F0;
    min-width: 120px;
    white-space: nowrap;
    box-sizing: border-box;
    overflow: visible;
    font-size: 10pt;

    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;

    &:first-child {
      @apply pl-10;
    }
  }
}

</style>

